import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export { gsap, ScrollTrigger };

/** Live MediaQueryList per query — see matchesMedia(). */
const mediaCache = new Map();

/**
 * A MediaQueryList stays live, so one object per query answers for the whole
 * session. Reading `.matches` never flushes layout, which matters because these
 * helpers are called from render paths and from effects that have just mutated
 * the DOM.
 */
function matchesMedia(query) {
  if (typeof window === "undefined") return false;
  let list = mediaCache.get(query);
  if (!list) {
    list = window.matchMedia(query);
    mediaCache.set(query, list);
  }
  return list.matches;
}

/** True when the visitor has asked the OS to reduce motion. */
export const prefersReducedMotion = () =>
  matchesMedia("(prefers-reduced-motion: reduce)");

/**
 * Coarse pointer / small viewport — we downgrade heavy effects here.
 *
 * The width half is a media query rather than `window.innerWidth < 900` because
 * Button asks on every render and innerWidth forces a synchronous layout. Media
 * query width includes the scrollbar exactly like innerWidth does, so the
 * breakpoint lands on the same pixel.
 */
export const isTouch = () =>
  matchesMedia("(hover: none)") || matchesMedia("(max-width: 899.98px)");

export const EASE = "power3.out";
export const EASE_EXPO = "expo.out";

/**
 * Wrap every word of an element in `<span class="word"><span class="word__i">`
 * so the inner span can be translated behind an overflow-hidden mask.
 *
 * Returns the array of inner spans, or `null` if the node was already split.
 */
export function splitWords(el) {
  if (!el || el.dataset.split === "true") return null;

  // Read once, then build the whole structure inside a fragment: the live tree
  // is touched a single time at the end instead of once per word, so the layout
  // the browser has already computed stays valid until that last statement.
  const source = el.textContent;
  const inners = [];
  const frag = document.createDocumentFragment();

  source.split(/(\s+)/).forEach((token) => {
    if (!token.trim()) {
      frag.appendChild(document.createTextNode(" "));
      return;
    }
    const outer = document.createElement("span");
    outer.className = "word";
    outer.setAttribute("aria-hidden", "true");

    const inner = document.createElement("span");
    inner.className = "word__i";
    inner.textContent = token;

    outer.appendChild(inner);
    frag.appendChild(outer);
    inners.push(inner);
  });

  el.dataset.split = "true";
  el.setAttribute("aria-label", source);
  el.replaceChildren(frag);

  return inners;
}

/**
 * Word reveals are queued and flushed in two passes — every split first, then
 * every tween.
 *
 * Splitting writes to the DOM and building the tween makes GSAP read computed
 * style back off the same element, so doing both per heading made each of the
 * page's headings pay its own forced synchronous reflow. Batched, the browser
 * has one layout to compute for the whole set.
 *
 * The flush is a microtask, so it still runs inside the task that queued it —
 * before the browser recalculates style, lays out or paints anything, and
 * before gsap.ticker advances. Start times, easings and visuals are exactly
 * what they were when this ran inline.
 */
const pendingReveals = [];
let revealFlushQueued = false;

function animateWords(el, inners, { trigger, delay, stagger }) {
  gsap.set(el, { opacity: 1 });
  gsap.fromTo(
    inners,
    { yPercent: 118, rotate: 3 },
    {
      yPercent: 0,
      rotate: 0,
      duration: 1.05,
      ease: EASE_EXPO,
      stagger,
      delay,
      scrollTrigger: trigger
        ? { trigger: trigger || el, start: "top 85%", once: true }
        : undefined,
    }
  );
}

function flushReveals() {
  revealFlushQueued = false;
  // Drain first: creating a tween can queue more work.
  const queued = pendingReveals.splice(0);
  const ready = [];

  // Pass 1 — writes only, no measuring in between.
  queued.forEach((item) => {
    if (!item.el.isConnected) return;
    item.inners = splitWords(item.el);
    if (item.inners) ready.push(item);
  });

  // Pass 2 — the reads, against a layout that is now settled for every heading.
  ready.forEach(({ el, inners, opts, ctx }) => {
    const run = () => animateWords(el, inners, opts);
    // The caller built us inside a gsap.context() that owns cleanup, and we are
    // no longer on its stack — re-enter it so the tween is still collected, and
    // drop anything whose context reverted while the flush was pending.
    if (!ctx) run();
    else if (!ctx.isReverted) ctx.add(run);
  });
}

/**
 * Masked, staggered word reveal driven by ScrollTrigger.
 * Falls back to a plain fade when motion is reduced.
 */
export function revealWords(el, { trigger, delay = 0, stagger = 0.055 } = {}) {
  if (!el) return;

  if (prefersReducedMotion()) {
    gsap.set(el, { opacity: 1 });
    return;
  }

  pendingReveals.push({
    el,
    inners: null,
    opts: { trigger, delay, stagger },
    // Captured while we are still on the caller's stack; see flushReveals().
    ctx: gsap.core.context(),
  });

  if (!revealFlushQueued) {
    revealFlushQueued = true;
    queueMicrotask(flushReveals);
  }
}

/** Simple scroll-in for blocks that don't need per-word treatment. */
export function revealBlock(el, { y = 34, delay = 0, duration = 0.9 } = {}) {
  if (!el) return;
  if (prefersReducedMotion()) {
    gsap.set(el, { opacity: 1, y: 0 });
    return;
  }
  gsap.fromTo(
    el,
    { opacity: 0, y },
    {
      opacity: 1,
      y: 0,
      duration,
      ease: EASE,
      delay,
      scrollTrigger: { trigger: el, start: "top 88%", once: true },
    }
  );
}

/** Clip-path wipe used for photography / product shots. */
export function revealImage(el, { delay = 0 } = {}) {
  if (!el) return;
  if (prefersReducedMotion()) {
    gsap.set(el, { clipPath: "none", opacity: 1 });
    return;
  }
  gsap.fromTo(
    el,
    { clipPath: "inset(0% 0% 100% 0%)", scale: 1.08, opacity: 1 },
    {
      clipPath: "inset(0% 0% 0% 0%)",
      scale: 1,
      duration: 1.3,
      ease: EASE_EXPO,
      delay,
      scrollTrigger: { trigger: el, start: "top 85%", once: true },
    }
  );
}
